import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { PhoneInput } from '@/components/PhoneInput'
import { useRecaptcha } from '@/hooks/useRecaptcha'
import { loginApi } from '@/api/auth.api'
import { AuthErrorCode } from '@/types/auth.types'
import { HumanCaptchaModal } from '@/components/HumanCaptchaModal'

export function Login() {
  const navigate = useNavigate()
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>()
  const [isValid, setIsValid] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Human reCAPTCHA state
  const [requiresHumanCaptcha, setRequiresHumanCaptcha] = useState(false)
  const [humanCaptchaSiteKey, setHumanCaptchaSiteKey] = useState<string | null>(null)
  const [, setHumanCaptchaToken] = useState<string | null>(null)
  const [captchaError, setCaptchaError] = useState<string | null>(null)

  const processLoginResponse = useCallback(
    async (recaptchaToken: string, isHumanCaptcha = false): Promise<{ success: boolean; error: string; requiresHumanCaptcha: boolean }> => {
      if (!phoneNumber) return { success: false, error: 'Missing phone number', requiresHumanCaptcha: false }

      try {
        const response = await loginApi({
          phoneNumber,
          recaptchaToken,
          isHumanCaptcha,
        })

        if (response.success) {
          navigate('/login/otp', { state: { phoneNumber } })
          return { success: true, error: '', requiresHumanCaptcha: false }
        }

        const result = { success: false, error: 'Something went wrong, please try again', requiresHumanCaptcha: false }

        switch (response.errorCode) {
          case AuthErrorCode.InvalidVerificationCode:
            result.error = 'Invalid phone number format'
            break
          case AuthErrorCode.RateLimitExceeded:
            result.error = 'Too many attempts. Please try again later'
            break
          case AuthErrorCode.RecaptchaFailed:
            if (response.requiresHumanCaptcha && response.humanCaptchaSiteKey) {
              setRequiresHumanCaptcha(true)
              setHumanCaptchaSiteKey(response.humanCaptchaSiteKey)
              result.requiresHumanCaptcha = true
            } else {
              result.error = 'Verification failed. Please try again'
            }
            break
          case AuthErrorCode.VerificationCodeSendFailed:
            result.error = 'Failed to send code. Please try again'
            break
          case AuthErrorCode.GetUserFailed:
            result.error = 'Failed to retrieve user data. Please try again'
            break
        }

        if (!result.requiresHumanCaptcha) {
          setErrorMessage(result.error)
        }
        return result
      } catch {
        return { success: false, error: 'Something went wrong, please try again', requiresHumanCaptcha: false }
      }
    },
    [phoneNumber, navigate]
  )

  const handleHumanCaptchaComplete = useCallback(
    async (token: string) => {
      if (!phoneNumber || !token) return

      setLoading(true)
      setErrorMessage(null)

      try {
        const result = await processLoginResponse(token, true)
        if (result.success) {
          setRequiresHumanCaptcha(false)
          setHumanCaptchaSiteKey(null)
          setHumanCaptchaToken(null)
        } else if (result.requiresHumanCaptcha) {
          setCaptchaError(result.error)
        } else {
          setErrorMessage(result.error || 'Failed to send code')
        }
      } catch {
        setErrorMessage('Failed to send code')
      } finally {
        setLoading(false)
      }
    },
    [phoneNumber, processLoginResponse]
  )

  const { getToken, resetRecaptcha, handleBackFromHumanCaptcha } = useRecaptcha({
    requiresHumanCaptcha,
    humanCaptchaSiteKey,
    onHumanCaptchaComplete: handleHumanCaptchaComplete,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return

    setLoading(true)
    setErrorMessage(null)

    const recaptchaToken = await getToken('affiliateLogin')
    if (!recaptchaToken) {
      setErrorMessage('Failed to get security token. Please try again.')
      setLoading(false)
      return
    }

    const result = await processLoginResponse(recaptchaToken)
    if (result.requiresHumanCaptcha || !result.success) {
      setLoading(false)
    }
  }

  const handleBackFromHumanCaptchaLocal = () => {
    handleBackFromHumanCaptcha(
      setRequiresHumanCaptcha,
      setHumanCaptchaSiteKey,
      setCaptchaError,
      setHumanCaptchaToken
    )
  }

  const handleRetryHumanCaptchaLocal = () => {
    resetRecaptcha(setCaptchaError)
  }

  return (
    <>
      <div className="flex min-h-screen flex-col items-center justify-center px-6">
        <div className="w-full max-w-sm space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold">Mezza Affiliate</h1>
            <p className="mt-2 text-muted-foreground">
              Sign in to access your affiliate dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="phone" className="text-sm font-medium">
                Phone Number
              </label>
              <PhoneInput
                value={phoneNumber}
                onChange={setPhoneNumber}
                defaultCountry="AE"
                error={errorMessage}
                onValidChange={setIsValid}
              />
            </div>

            <Button
              type="submit"
              className="h-12 w-full rounded-xl text-base font-bold"
              disabled={loading || !isValid}
            >
              {loading ? 'Sending...' : 'Send OTP'}
            </Button>
          </form>
        </div>
      </div>

      {requiresHumanCaptcha && humanCaptchaSiteKey ? (
        <HumanCaptchaModal
          siteKey={humanCaptchaSiteKey}
          actionText="continue"
          onBack={handleBackFromHumanCaptchaLocal}
          onRetry={handleRetryHumanCaptchaLocal}
          showRetry={!!captchaError}
          errorMessage={captchaError || undefined}
          isSubmitting={loading}
        />
      ) : null}
    </>
  )
}
